import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, StatusBadge, Empty, Select } from "./ui";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const REASONS = ["faulty", "unused", "recovered"];
const DISPOSITIONS = ["restock", "repair", "write_off"];

export default function Returns() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ serial: "", reason: "faulty" });
  const [disp, setDisp] = useState({});

  const canCreate = ["fitment_technician", "regional_manager", "operations_controller", "system_admin"].includes(user.role);
  const canReceive = ["warehouse_manager", "operations_controller", "system_admin"].includes(user.role);

  const load = useCallback(() => { api.get("/returns").then((r) => setList(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.serial) { toast.error("Serial required"); return; }
    try { await api.post("/returns", form); toast.success("Return initiated"); setForm({ serial: "", reason: "faulty" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const receive = async (id) => {
    try { await api.post(`/returns/${id}/receive`, { disposition: disp[id] || "restock" }); toast.success("Return received & dispositioned"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Returns" subtitle="Recover faulty, unused or removed units and disposition them." />
      {canCreate && (
        <Panel title="Initiate Return" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div><Label className="overline text-slate-500">Serial</Label><Input data-testid="ret-serial" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} className="rounded-sm mt-1 font-label" /></div>
            <div><Label className="overline text-slate-500">Reason</Label><div className="mt-1"><Select testid="ret-reason" value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} options={REASONS.map((r) => ({ value: r, label: r }))} placeholder="Reason" /></div></div>
            <Button data-testid="ret-submit" onClick={create} className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">Initiate Return</Button>
          </div>
        </Panel>
      )}
      <Panel title="Returns">
        {list.length === 0 ? <Empty>No returns.</Empty> : (
          <table className="w-full text-sm" data-testid="returns-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Serial</th><th className="py-2 pr-3">Reason</th><th className="py-2 pr-3">From</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Disposition</th><th></th></tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-label text-[13px]">{r.serial}</td>
                  <td className="py-2 pr-3 text-slate-600">{r.reason}</td>
                  <td className="py-2 pr-3 text-slate-500">{r.from_location || "—"}</td>
                  <td className="py-2 pr-3"><StatusBadge status={r.status === "in_transit" ? "return_in_transit" : "closed"} /></td>
                  <td className="py-2 pr-3">
                    {r.status === "in_transit" && canReceive ? (
                      <Select testid={`disp-${r.id}`} value={disp[r.id] || ""} onChange={(v) => setDisp({ ...disp, [r.id]: v })} options={DISPOSITIONS.map((d) => ({ value: d, label: d.replace("_", " ") }))} placeholder="disposition" />
                    ) : (r.disposition ? r.disposition.replace("_", " ") : "—")}
                  </td>
                  <td className="py-2 text-right">{r.status === "in_transit" && canReceive && (
                    <Button data-testid={`receive-${r.id}`} onClick={() => receive(r.id)} size="sm" className="rounded-sm bg-[#0B2545] h-7 text-xs">Receive</Button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
